import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  ChevronRight, 
  ChevronDown, 
  Download, 
  Search, 
  Bell, 
  Filter,
  DollarSign,
  PieChart as PieChartIcon,
  BarChart3,
  Calendar,
  Globe,
  Plus,
  Minus
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  Legend
} from 'recharts';
import { initialTimesheetEntries, wbsCodes, financialData, sCurveData, users, getCompanyFromEmail } from '../dummyData';
import { downloadCSV, downloadXLSX, formatSAPDate } from '../utils/exportUtils';
import { formatNumber, formatCurrency } from '../utils/formatUtils';

const KPICard = ({ label, value, trend, comparison, status }) => {
  const isPositive = status === 'positive';
  const isNegative = status === 'negative';
  
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow">
      <div className={`absolute top-0 left-0 w-1 h-full ${
        isPositive ? 'bg-emerald-500' : isNegative ? 'bg-rose-500' : 'bg-indigo-500'
      }`} />
      <div className="flex justify-between items-start mb-4">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
          isPositive ? 'bg-emerald-50 text-emerald-600' : isNegative ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-600'
        }`}>
          {isPositive ? <Plus className="w-3 h-3" /> : isNegative ? <Minus className="w-3 h-3" /> : null}
          {trend}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-3xl font-bold text-slate-800 tracking-tight">{value}</h3>
        <p className="text-xs font-medium text-slate-400">
          vs. {comparison.label}: <span className="text-slate-500 font-bold">{comparison.value}</span>
        </p>
      </div>
    </div>
  );
};

const MiniTrend = ({ data }) => (
  <div className="flex items-end gap-[2px] h-8 w-16">
    {data.map((val, i) => (
      <div 
        key={i} 
        style={{ height: `${(val / (Math.max(...data) || 1)) * 100}%` }} 
        className={`w-1.5 rounded-t-sm transition-all duration-500 ${
            i === data.length - 1 ? 'bg-indigo-600' : 'bg-indigo-200 group-hover:bg-indigo-300'
        }`}
      />
    ))}
  </div>
);

const GroupingManager = ({ levels, onLevelsChange }) => {
  const allLevels = ['Phase', 'Zone', 'Entity', 'Personnel', 'WBS', 'Scope'];
  
  return (
    <div className="flex items-center gap-2 bg-slate-100/50 p-1.5 rounded-xl border border-slate-200">
      {levels.map((level, i) => (
        <div key={level} className="flex items-center gap-1 bg-white px-3 py-1 rounded-lg shadow-sm border border-slate-200">
          <span className="text-xs font-bold text-slate-700">{level}</span>
          <button 
            onClick={() => onLevelsChange(levels.filter(l => l !== level))}
            className="text-slate-400 hover:text-rose-500 transition-colors"
          >
            <Minus className="w-3 h-3" />
          </button>
          {i < levels.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 transition-transform group-hover:translate-x-0.5" />}
        </div>
      ))}
      <select 
        className="bg-transparent text-[10px] font-bold text-slate-500 outline-none cursor-pointer uppercase tracking-widest px-2 hover:text-indigo-600 transition-colors"
        onChange={(e) => {
          if (e.target.value && !levels.includes(e.target.value)) {
            onLevelsChange([...levels, e.target.value]);
          }
          e.target.value = '';
        }}
      >
        <option value="">+ Add Level</option>
        {allLevels.filter(l => !levels.includes(l)).map(l => (
          <option key={l} value={l}>{l}</option>
        ))}
      </select>
    </div>
  );
};

const TableRow = ({ item, depth = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <>
      <tr className={`group border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${item.isHeader ? 'font-bold' : ''}`}>
        <td className="px-6 py-4">
          <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 20}px` }}>
            {hasChildren && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-0.5 hover:bg-slate-200 rounded transition-colors"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              </button>
            )}
            {!hasChildren && depth > 0 && <div className="w-5" />}
            <span className={`text-sm ${item.isHeader ? 'text-slate-800' : 'text-slate-600 font-medium'}`}>{item.name}</span>
          </div>
        </td>
        <td className="px-6 py-4 text-right text-sm font-bold text-slate-800">{formatCurrency(item.actual)}</td>
        <td className="px-6 py-4 text-right text-sm text-slate-500 font-medium">{formatCurrency(item.budget)}</td>
        <td className={`px-6 py-4 text-right text-sm font-bold ${item.cpi >= 1 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {item.cpi.toFixed(2)}
        </td>
        <td className={`px-6 py-4 text-right text-sm font-bold ${item.spi >= 1 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {item.spi.toFixed(2)}
        </td>
        <td className="px-6 py-4 text-right text-sm text-indigo-700 font-black">{formatCurrency(item.eac)}</td>
        <td className="px-6 py-4 text-right">
            <div className="flex justify-end">
                {item.trend ? <MiniTrend data={item.trend} /> : <div className="h-0.5 w-12 bg-slate-200 rounded-full" />}
            </div>
        </td>
      </tr>
      {isExpanded && hasChildren && item.children.map((child, i) => (
        <TableRow key={i} item={child} depth={depth + 1} />
      ))}
    </>
  );
};

export default function ConsolidatedReport() {
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [period, setPeriod] = useState('Q1 (Jan-Mar)');
  const [currency, setCurrency] = useState('USD ($)');
  const [comparison, setComparison] = useState('Budget');
  const [groupLevels, setGroupLevels] = useState(['Phase', 'Zone']);

  const reportTable = useMemo(() => {
    const buildHierarchy = (entries, currentLevels) => {
      if (currentLevels.length === 0) return [];
      
      const [currentLevel, ...remainingLevels] = currentLevels;
      const groups = {};
      
      entries.forEach(entry => {
        const wbs = wbsCodes.find(w => w.code === entry.wbsCode);
        if (!wbs) return;
        
        let key = '';
        if (currentLevel === 'Phase') key = wbs.phase;
        else if (currentLevel === 'Zone') key = wbs.zone;
        else if (currentLevel === 'Entity') key = getCompanyFromEmail(users.find(u => u.id === entry.userId)?.email) || 'Unknown';
        else if (currentLevel === 'Personnel') key = users.find(u => u.id === entry.userId)?.name || 'Unknown User';
        else if (currentLevel === 'WBS') key = wbs.code;
        else if (currentLevel === 'Scope') key = wbs.scope;

        if (!groups[key]) {
          groups[key] = { name: key, entries: [], actual: 0, budget: 0, trend: Array(6).fill(0), bac: 0 };
        }
        
        const laborCost = entry.hours * 150;
        groups[key].entries.push(entry);
        groups[key].actual += laborCost;
        groups[key].budget += laborCost * 0.95;
        groups[key].bac += (wbs.bac / 100) * (entry.hours / 40);
        
        const month = new Date(entry.date).getMonth();
        groups[key].trend[month % 6] += laborCost;
      });

      return Object.entries(groups).map(([name, data]) => {
        const children = buildHierarchy(data.entries, remainingLevels);
        const ev = data.actual * (0.95 + Math.random() * 0.1);
        const cpi = ev / (data.actual || 1);
        const spi = ev / (data.budget || 1);

        return {
          name,
          actual: data.actual,
          budget: data.budget,
          variance: data.actual - data.budget,
          variancePct: ((data.actual - data.budget) / (data.budget || 1)) * 100,
          lastYear: data.actual * 0.8,
          trend: data.trend,
          ev,
          cpi,
          spi,
          bac: data.bac,
          eac: data.bac / (cpi || 1),
          children,
          isHeader: true
        };
      });
    };

    return buildHierarchy(initialTimesheetEntries, groupLevels);
  }, [groupLevels]);

  const exportReport = () => {
    // Flatten hierarchy for CSV export
    const rows = [];
    const flatten = (items, path = []) => {
      items.forEach(item => {
        const currentPath = [...path, item.name].join(' > ');
        rows.push([
          currentPath,
          item.actual.toFixed(2),
          item.budget.toFixed(2),
          item.variance.toFixed(2),
          item.cpi.toFixed(2),
          item.spi.toFixed(2),
          item.eac.toFixed(2)
        ]);
        if (item.children) flatten(item.children, [...path, item.name]);
      });
    };

    flatten(reportTable);

    const headers = ['Hierarchy Path', 'Actual Cost (AC)', 'Planned Value (PV)', 'Cost Variance (CV)', 'CPI', 'SPI', 'EAC'];
    downloadXLSX(headers, rows, `Financial_Report_${formatSAPDate(new Date())}.xlsx`, 'Financials');
  };

  return (
    <div className="min-h-screen bg-slate-50/30 flex flex-col gap-8 pb-12">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Project Controls & Financial Performance</h1>
            <div className="flex items-center gap-4">
                <button 
                  onClick={exportReport}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md hover:bg-indigo-700 transition-all"
                >
                   <Download className="w-4 h-4" /> EXPORT EXCEL (.xlsx)
                </button>
            </div>
        </div>

        <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-6 px-4 py-2">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">Fiscal Year:</span>
                    <select value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} className="text-sm font-bold bg-slate-100 rounded-lg px-2 py-1 outline-none">
                        <option>2026</option><option>2025</option><option>2024</option>
                    </select>
                </div>
                <div className="h-8 w-px bg-slate-100" />
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Reports Stack (Grouping):</span>
                    <GroupingManager levels={groupLevels} onLevelsChange={setGroupLevels} />
                </div>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {financialData.kpis.map((kpi, i) => (
          <KPICard 
            key={i} 
            label={kpi.label} 
            value={kpi.value} 
            trend={kpi.trend} 
            status={kpi.status} 
            comparison={{ label: 'Target', value: kpi.vsBudget || kpi.vsPY || kpi.roa }} 
          />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-800 tracking-tight">Performance Breakdown (EVM)</h2>
                </div>
            </div>
            
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200">
                            <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Hierarchy</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Actual (AC)</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Planned (PV)</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest underline decoration-green-400/50">CPI</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest underline decoration-blue-400/50">SPI</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">EAC (Projected)</th>
                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Trend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportTable.map((item, i) => <TableRow key={i} item={item} />)}
                    </tbody>
                </table>
            </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col">
            <div className="flex justify-between items-center mb-8">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">Project S-Curve</h3>
                <div className="flex gap-2">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                        <span className="text-[10px] font-bold text-slate-500">PV</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                        <span className="text-[10px] font-bold text-slate-500">EV</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                        <span className="text-[10px] font-bold text-slate-500">AC</span>
                    </div>
                </div>
            </div>
            
            <div className="flex-1">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={sCurveData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }} tickFormatter={v => `$${v}M`} />
                        <Tooltip 
                            cursor={{ fill: '#f8fafc' }}
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                        />
                        <Bar dataKey="pv" fill="#e2e8f0" radius={[4, 4, 0, 0]} barSize={8} />
                        <Bar dataKey="ev" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={8} />
                        <Bar dataKey="ac" fill="#f43f5e" radius={[4, 4, 0, 0]} barSize={8} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-100/50">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                    <span className="font-bold text-slate-700">Analytics Note:</span> The project is currently showing a <span className="text-emerald-600 font-bold">1.04 CPI</span>, indicating we are performing 4% under budget. However, scheduling lags in Phase 3 are reflected in the <span className="text-amber-600 font-bold">0.98 SPI</span>.
                </p>
            </div>
        </div>
      </div>
    </div>
  );
}
